/*
 * Built-in seeds for the Event Configuration "E-mail" tab.
 *
 * These mirror the hardcoded message bodies in the legacy PHP mailers
 * (eplus/_clients/<client>/*_sendaccountinfo.php and friends). When a template
 * has no stored value yet, the editor is pre-filled with the matching seed
 * below so the superadmin starts from the email's current wording instead of
 * an empty box — and only has to tweak it.
 *
 * The PHP `$variables` were converted to {{ token }} placeholders that are
 * filled at send time (see EmailTemplateService on the API side and
 * lc_email.inc.php / getEmailTemplate() on the legacy side). The available
 * tokens per template are declared in `emailBlocks` in the component.
 */

export interface EmailDefault {
  subject: string;
  body_html: string;
}

export const EMAIL_DEFAULTS: { [key: string]: { [lang: string]: EmailDefault } } = {
  // ChangePwdAction.php / MailerChangePwd.php — confirmation sent to a user
  // after their password was changed (public reset-pwd flow).
  'change-pwd': {
    de: {
      subject: '{{ event_title }} - Ihr Passwort wurde geändert',
      body_html: `<p>Hallo {{ first_name }} {{ last_name }},</p>
<p>Ihr Passwort für {{ event_title }} wurde erfolgreich geändert.</p>
<p>Falls Sie diese Änderung nicht veranlasst haben, kontaktieren Sie uns bitte umgehend, damit wir das gemeinsam prüfen können.</p>
<p><br>Mit freundlichen Grüßen,<br>Ihr {{ event_title }} Team</p>`,
    },
    en: {
      subject: '{{ event_title }} - Your password was changed',
      body_html: `<p>Hello {{ first_name }} {{ last_name }},</p>
<p>Your password for {{ event_title }} was changed successfully.</p>
<p>If you did not request this change, please contact us right away so we can look into it together.</p>
<p><br>Best regards,<br>Your {{ event_title }} team</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Votre mot de passe a été modifié',
      body_html: `<p>Bonjour {{ first_name }} {{ last_name }},</p>
<p>Votre mot de passe pour {{ event_title }} a été modifié avec succès.</p>
<p>Si vous n'êtes pas à l'origine de cette modification, veuillez nous contacter immédiatement afin que nous puissions vérifier ensemble.</p>
<p><br>Cordialement,<br>Votre équipe {{ event_title }}</p>`,
    },
  },

  // MailAction.php / Mailer.php — support/contact message a logged-in user
  // sends to the organising team; the team receives the message plus the
  // sender's details.
  contact: {
    de: {
      subject: '{{ subject }}',
      body_html: `<p>{{ subject }}</p>
<p>{{ message }}</p>
<p>Information zum Benutzer:<br>
Benutzer: {{ first_name }} {{ last_name }}<br>
ID: {{ userid }}<br>
Telefon: {{ phone }}<br>
E-Mail: {{ email }}</p>`,
    },
    en: {
      subject: '{{ subject }}',
      body_html: `<p>{{ subject }}</p>
<p>{{ message }}</p>
<p>User information:<br>
User: {{ first_name }} {{ last_name }}<br>
ID: {{ userid }}<br>
Phone: {{ phone }}<br>
E-Mail: {{ email }}</p>`,
    },
    fr: {
      subject: '{{ subject }}',
      body_html: `<p>{{ subject }}</p>
<p>{{ message }}</p>
<p>Informations sur l'utilisateur :<br>
Utilisateur : {{ first_name }} {{ last_name }}<br>
ID : {{ userid }}<br>
Téléphone : {{ phone }}<br>
E-mail : {{ email }}</p>`,
    },
  },

  // exvisitor_sendaccountinfo.php — credentials/welcome mail sent to the
  // participant once their account is created.
  'send-account-info': {
    de: {
      subject: '{{ event_title }} - Ihre Zugangsdaten',
      body_html: `<p>Liebe.r {{ salutation }},</p>
<p>Um Ihre Anmeldung zum Workshop {{ event_title }} zu bestätigen und Ihre Termine für den ersten Vormittag der Fachtreffen zu planen, füllen Sie bitte Ihr Profil auf der folgenden Website <a href="https://{{ event_domain }}">{{ event_domain }}</a> bis spätestens <strong>{{ validate_registration_latest }}</strong> aus.</p>
<p>Hier sind Ihre Zugangsdaten:</p>
<p>- Benutzername: <strong>{{ username }}</strong><br>- Passwort: <strong>{{ password }}</strong></p>
<p>Bitte zögern Sie nicht, uns zu kontaktieren, wenn Sie Fragen haben.</p>
<p><br>Mit herzlichem Dank im Voraus,<br>Team Verkaufsförderung von Atout France Deutschland.</p>`,
    },
    en: {
      subject: '{{ event_title }} - Your Account-Info',
      body_html: `<p>Dear {{ salutation }},</p>
<p>In order to validate your registration for {{ event_title }}, please complete your profile on <a href="https://{{ event_domain }}">{{ event_domain }}</a> by <strong>{{ validate_registration_latest }}</strong>.</p>
<p>Here are your login details:</p>
<p>- Username: <strong>{{ username }}</strong><br>- Password: <strong>{{ password }}</strong></p>
<p>Please do not hesitate to contact us if you have any question!</p>
<p><br>Best regards,<br>Your Atout France team in Austria / Central Europe – Germany / Poland / Switzerland.</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Vos identifiants',
      body_html: `<p>Cher/Chère {{ salutation }},</p>
<p>Afin de valider votre inscription pour {{ event_title }}, veuillez compléter votre profil sur <a href="https://{{ event_domain }}">{{ event_domain }}</a> avant le <strong>{{ validate_registration_latest }}</strong>.</p>
<p>Voici vos identifiants de connexion :</p>
<p>- Identifiant : <strong>{{ username }}</strong><br>- Mot de passe : <strong>{{ password }}</strong></p>
<p>N'hésitez pas à nous contacter si vous avez des questions.</p>
<p><br>Avec nos remerciements anticipés,<br>L'équipe Promotion Commerciale d'Atout France.</p>`,
    },
  },

  // exvisitor_approve.php / exvisitor_activate.php — visitor registration is
  // complete; credentials are sent so they can start booking appointments.
  'approve-visitor': {
    de: {
      subject: '{{ event_title }} - Ihre Anmeldung ist abgeschlossen',
      body_html: `<p>Hallo {{ salutation }},</p>
<p>Wir freuen uns, Ihnen mitteilen zu können, dass Ihre Anmeldung zu {{ event_title }} abgeschlossen ist.</p>
<p>Wir empfehlen Ihnen, sich jetzt auf <a href="https://{{ event_domain }}">{{ event_domain }}</a> anzumelden, um Ihre Termine mit den Ausstellern zu buchen.</p>
<p>- Benutzername: <strong>{{ username }}</strong><br>- Passwort: <strong>{{ password }}</strong></p>
<p>Wir freuen uns auf Ihre Teilnahme.</p>
<p><br>Mit freundlichen Grüßen,<br>Atout France</p>`,
    },
    en: {
      subject: '{{ event_title }} - Your Registration is complete',
      body_html: `<p>Hello {{ salutation }},</p>
<p>We are pleased to inform you that your registration for {{ event_title }} is complete.</p>
<p>We recommend that you log on to <a href="https://{{ event_domain }}">{{ event_domain }}</a> to book your meeting appointments with the exhibitors now.</p>
<p>- Username: <strong>{{ username }}</strong><br>- Password: <strong>{{ password }}</strong></p>
<p>We look forward to seeing you.</p>
<p><br>Best regards,<br>Atout France</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Votre inscription est complète',
      body_html: `<p>Bonjour {{ salutation }},</p>
<p>Nous avons le plaisir de vous informer que votre inscription pour {{ event_title }} est complète.</p>
<p>Nous vous recommandons de vous connecter dès maintenant sur <a href="https://{{ event_domain }}">{{ event_domain }}</a> pour réserver vos rendez-vous avec les exposants.</p>
<p>- Identifiant : <strong>{{ username }}</strong><br>- Mot de passe : <strong>{{ password }}</strong></p>
<p>Au plaisir de vous accueillir.</p>
<p><br>Cordialement,<br>Atout France</p>`,
    },
  },

  // vexhibitor_approve.php — exhibitor registration is complete; credentials
  // are sent so the exhibitor can start managing their agenda.
  'approve-exhibitor': {
    de: {
      subject: '{{ event_title }} - Ihre Anmeldung ist abgeschlossen',
      body_html: `<p>Hallo {{ salutation }},</p>
<p>Wir freuen uns, Ihnen mitteilen zu können, dass Ihre Anmeldung zu {{ event_title }} abgeschlossen ist.</p>
<p>Wir empfehlen Ihnen, sich jetzt auf <a href="https://{{ event_domain }}">{{ event_domain }}</a> anzumelden, um Ihre Termine zu verwalten.</p>
<p>- Benutzername: <strong>{{ username }}</strong><br>- Passwort: <strong>{{ password }}</strong></p>
<p>Wir freuen uns auf Ihre Teilnahme.</p>
<p><br>Mit freundlichen Grüßen,<br>Atout France</p>`,
    },
    en: {
      subject: '{{ event_title }} - Your Registration is complete',
      body_html: `<p>Hello {{ salutation }},</p>
<p>We are pleased to inform you that your registration for {{ event_title }} is complete.</p>
<p>We recommend that you log on to <a href="https://{{ event_domain }}">{{ event_domain }}</a> to manage your meeting appointments now.</p>
<p>- Username: <strong>{{ username }}</strong><br>- Password: <strong>{{ password }}</strong></p>
<p>We look forward to seeing you.</p>
<p><br>Best regards,<br>Atout France</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Votre inscription est complète',
      body_html: `<p>Bonjour {{ salutation }},</p>
<p>Nous avons le plaisir de vous informer que votre inscription pour {{ event_title }} est complète.</p>
<p>Nous vous recommandons de vous connecter dès maintenant sur <a href="https://{{ event_domain }}">{{ event_domain }}</a> pour gérer vos rendez-vous.</p>
<p>- Identifiant : <strong>{{ username }}</strong><br>- Mot de passe : <strong>{{ password }}</strong></p>
<p>Au plaisir de vous accueillir.</p>
<p><br>Cordialement,<br>Atout France</p>`,
    },
  },

  // exvisitor_sendreminder.php — reminder to a visitor to complete their
  // profile before the registration validation deadline.
  'send-reminder': {
    de: {
      subject: '{{ event_title }} - bitte vervollständigen Sie Ihr Profil',
      body_html: `<p>Liebe.r {{ salutation }},</p>
<p>Wir möchten Sie daran erinnern, Ihr Profil auf <a href="https://{{ event_domain }}">{{ event_domain }}</a> bis spätestens <strong>{{ validate_registration_latest }}</strong> auszufüllen, um Ihre Anmeldung zu {{ event_title }} zu bestätigen. Vielen Dank!</p>
<p>Hier sind Ihre Zugangsdaten:</p>
<p>- Benutzername: <strong>{{ username }}</strong><br>- Passwort: <strong>{{ password }}</strong></p>
<p>Bitte zögern Sie nicht, uns zu kontaktieren, wenn Sie Fragen haben.</p>
<p><br>Mit freundlichen Grüßen,<br>Ihr Atout France Team</p>`,
    },
    en: {
      subject: '{{ event_title }} - please complete your profile',
      body_html: `<p>Dear {{ salutation }},</p>
<p>We remind you that you have to complete your profile on <a href="https://{{ event_domain }}">{{ event_domain }}</a> by <strong>{{ validate_registration_latest }}</strong> in order to validate your registration for {{ event_title }}, thank you!</p>
<p>Here are your login details:</p>
<p>- Username: <strong>{{ username }}</strong><br>- Password: <strong>{{ password }}</strong></p>
<p>Please do not hesitate to contact us if you have any question!</p>
<p><br>Best regards,<br>Your Atout France team</p>`,
    },
    fr: {
      subject: '{{ event_title }} - veuillez compléter votre profil',
      body_html: `<p>Cher/Chère {{ salutation }},</p>
<p>Nous vous rappelons que vous devez compléter votre profil sur <a href="https://{{ event_domain }}">{{ event_domain }}</a> avant le <strong>{{ validate_registration_latest }}</strong> afin de valider votre inscription pour {{ event_title }}, merci !</p>
<p>Voici vos identifiants de connexion :</p>
<p>- Identifiant : <strong>{{ username }}</strong><br>- Mot de passe : <strong>{{ password }}</strong></p>
<p>N'hésitez pas à nous contacter si vous avez des questions.</p>
<p><br>Cordialement,<br>Votre équipe Atout France</p>`,
    },
  },

  // vexhibitor_sendreminder.php — reminder to an exhibitor to complete their
  // profile before the profile-completion deadline.
  'send-reminder-exhibitor': {
    de: {
      subject: '{{ event_title }} - bitte vervollständigen Sie Ihr Profil',
      body_html: `<p>Liebe.r {{ salutation }},</p>
<p>Wir möchten Sie daran erinnern, Ihr Profil auf <a href="https://{{ event_domain }}">{{ event_domain }}</a> bis spätestens <strong>{{ complete_profile_by }}</strong> auszufüllen, um Ihre Anmeldung zu {{ event_title }} zu bestätigen. Vielen Dank!</p>
<p>Hier sind Ihre Zugangsdaten:</p>
<p>- Benutzername: <strong>{{ username }}</strong><br>- Passwort: <strong>{{ password }}</strong></p>
<p>Bitte zögern Sie nicht, uns zu kontaktieren, wenn Sie Fragen haben.</p>
<p><br>Mit freundlichen Grüßen,<br>Ihr Atout France Team</p>`,
    },
    en: {
      subject: '{{ event_title }} - please complete your profile',
      body_html: `<p>Dear {{ salutation }},</p>
<p>We remind you that you have to complete your profile on <a href="https://{{ event_domain }}">{{ event_domain }}</a> by <strong>{{ complete_profile_by }}</strong> in order to validate your registration for {{ event_title }}, thank you!</p>
<p>Here are your login details:</p>
<p>- Username: <strong>{{ username }}</strong><br>- Password: <strong>{{ password }}</strong></p>
<p>Please do not hesitate to contact us if you have any question!</p>
<p><br>Best regards,<br>Your Atout France team</p>`,
    },
    fr: {
      subject: '{{ event_title }} - veuillez compléter votre profil',
      body_html: `<p>Cher/Chère {{ salutation }},</p>
<p>Nous vous rappelons que vous devez compléter votre profil sur <a href="https://{{ event_domain }}">{{ event_domain }}</a> avant le <strong>{{ complete_profile_by }}</strong> afin de valider votre inscription pour {{ event_title }}, merci !</p>
<p>Voici vos identifiants de connexion :</p>
<p>- Identifiant : <strong>{{ username }}</strong><br>- Mot de passe : <strong>{{ password }}</strong></p>
<p>N'hésitez pas à nous contacter si vous avez des questions.</p>
<p><br>Cordialement,<br>Votre équipe Atout France</p>`,
    },
  },

  // vexhibitor_sendaccountinfo.php — credentials/welcome mail sent to the
  // exhibitor with the profile-completion deadline.
  'send-account-info-exhibitor': {
    de: {
      subject: '{{ event_title }} - Ihre Zugangsdaten',
      body_html: `<p>Liebe.r {{ salutation }},</p>
<p>Um Ihre Anmeldung zu {{ event_title }} zu bestätigen, füllen Sie bitte Ihr Profil auf <a href="https://{{ event_domain }}">{{ event_domain }}</a> bis spätestens <strong>{{ complete_profile_by }}</strong> aus.</p>
<p>Hier sind Ihre Zugangsdaten:</p>
<p>- Benutzername: <strong>{{ username }}</strong><br>- Passwort: <strong>{{ password }}</strong></p>
<p>Bitte zögern Sie nicht, uns zu kontaktieren, wenn Sie Fragen haben.</p>
<p><br>Mit freundlichen Grüßen,<br>Ihr Atout France Team</p>`,
    },
    en: {
      subject: '{{ event_title }} - Your Account-Info',
      body_html: `<p>Dear {{ salutation }},</p>
<p>In order to validate your registration for {{ event_title }}, please complete your profile on <a href="https://{{ event_domain }}">{{ event_domain }}</a> by <strong>{{ complete_profile_by }}</strong>.</p>
<p>Here are your login details:</p>
<p>- Username: <strong>{{ username }}</strong><br>- Password: <strong>{{ password }}</strong></p>
<p>Please do not hesitate to contact us if you have any question!</p>
<p><br>Best regards,<br>Your Atout France team</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Vos identifiants',
      body_html: `<p>Cher/Chère {{ salutation }},</p>
<p>Afin de valider votre inscription pour {{ event_title }}, veuillez compléter votre profil sur <a href="https://{{ event_domain }}">{{ event_domain }}</a> avant le <strong>{{ complete_profile_by }}</strong>.</p>
<p>Voici vos identifiants de connexion :</p>
<p>- Identifiant : <strong>{{ username }}</strong><br>- Mot de passe : <strong>{{ password }}</strong></p>
<p>N'hésitez pas à nous contacter si vous avez des questions.</p>
<p><br>Cordialement,<br>Votre équipe Atout France</p>`,
    },
  },

  // vexhibitor_createAppointment_save.php — notification sent to the visitor
  // when a new appointment is created.
  'create-appointment-visitor': {
    de: {
      subject: '{{ event_title }} - Neuer Termin',
      body_html: `<p>Liebe.r {{ visitor_name }},</p>
<p>Wir freuen uns, Ihnen mitteilen zu können, dass ein neuer Termin erstellt wurde.</p>
<p><strong>Zeit: {{ appointment_time }}<br>Aussteller: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Besucher: {{ visitor_name }} - {{ visitor_company }}<br>Datum: {{ city }}<br>Grund: {{ info }}.</strong></p>
<p><br>Mit freundlichen Grüßen,<br>Ihr Atout France Team</p>`,
    },
    en: {
      subject: '{{ event_title }} - New Appointment',
      body_html: `<p>Dear {{ visitor_name }},</p>
<p>We are pleased to inform you that a new appointment has been created.</p>
<p><strong>Time: {{ appointment_time }}<br>Exhibitor: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visitor: {{ visitor_name }} - {{ visitor_company }}<br>Date: {{ city }}<br>Reason: {{ info }}.</strong></p>
<p><br>Best regards,<br>Your Atout France team</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Nouveau rendez-vous',
      body_html: `<p>Cher/Chère {{ visitor_name }},</p>
<p>Nous avons le plaisir de vous informer qu'un nouveau rendez-vous a été créé.</p>
<p><strong>Heure : {{ appointment_time }}<br>Exposant : {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visiteur : {{ visitor_name }} - {{ visitor_company }}<br>Date : {{ city }}<br>Motif : {{ info }}.</strong></p>
<p><br>Cordialement,<br>Votre équipe Atout France</p>`,
    },
  },

  // vexhibitor_createAppointment_save.php — notification sent to the exhibitor
  // when a new appointment is created.
  'create-appointment-exhibitor': {
    de: {
      subject: '{{ event_title }} - Neuer Termin',
      body_html: `<p>Liebe.r {{ exhibitor_name }},</p>
<p>Wir freuen uns, Ihnen mitteilen zu können, dass ein neuer Termin erstellt wurde.</p>
<p><strong>Zeit: {{ appointment_time }}<br>Aussteller: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Besucher: {{ visitor_name }} - {{ visitor_company }}<br>Datum: {{ city }}<br>Grund: {{ info }}.</strong></p>
<p><br>Mit freundlichen Grüßen,<br>Ihr Atout France Team</p>`,
    },
    en: {
      subject: '{{ event_title }} - New Appointment',
      body_html: `<p>Dear {{ exhibitor_name }},</p>
<p>We are pleased to inform you that a new appointment has been created.</p>
<p><strong>Time: {{ appointment_time }}<br>Exhibitor: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visitor: {{ visitor_name }} - {{ visitor_company }}<br>Date: {{ city }}<br>Reason: {{ info }}.</strong></p>
<p><br>Best regards,<br>Your Atout France team</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Nouveau rendez-vous',
      body_html: `<p>Cher/Chère {{ exhibitor_name }},</p>
<p>Nous avons le plaisir de vous informer qu'un nouveau rendez-vous a été créé.</p>
<p><strong>Heure : {{ appointment_time }}<br>Exposant : {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visiteur : {{ visitor_name }} - {{ visitor_company }}<br>Date : {{ city }}<br>Motif : {{ info }}.</strong></p>
<p><br>Cordialement,<br>Votre équipe Atout France</p>`,
    },
  },

  // vexhibitor_cancelAppointment_save.php — notification sent to the visitor
  // when the exhibitor cancels an appointment.
  'cancel-appointment-visitor': {
    de: {
      subject: '{{ event_title }} - Termin abgesagt',
      body_html: `<p>Liebe.r {{ visitor_name }},</p>
<p>Leider müssen wir Ihnen mitteilen, dass {{ exhibitor_company }} Ihren Termin abgesagt hat.</p>
<p><strong>Zeit: {{ appointment_time }}<br>Aussteller: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Besucher: {{ visitor_name }} - {{ visitor_company }}<br>Datum: {{ city }}<br>Grund: {{ reason }}.</strong></p>
<p><br>Mit freundlichen Grüßen,<br>Atout France</p>`,
    },
    en: {
      subject: '{{ event_title }} - Cancel Appointment',
      body_html: `<p>Dear {{ visitor_name }},</p>
<p>We are sorry to inform you that {{ exhibitor_company }} has canceled your appointment.</p>
<p><strong>Time: {{ appointment_time }}<br>Exhibitor: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visitor: {{ visitor_name }} - {{ visitor_company }}<br>Date: {{ city }}<br>Reason: {{ reason }}.</strong></p>
<p><br>Best regards,<br>Atout France</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Rendez-vous annulé',
      body_html: `<p>Cher/Chère {{ visitor_name }},</p>
<p>Nous sommes désolés de vous informer que {{ exhibitor_company }} a annulé votre rendez-vous.</p>
<p><strong>Heure : {{ appointment_time }}<br>Exposant : {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visiteur : {{ visitor_name }} - {{ visitor_company }}<br>Date : {{ city }}<br>Motif : {{ reason }}.</strong></p>
<p><br>Cordialement,<br>Atout France</p>`,
    },
  },

  // vexhibitor_cancelAppointment_save.php — notification sent to the exhibitor
  // when the visitor cancels an appointment.
  'cancel-appointment-exhibitor': {
    de: {
      subject: '{{ event_title }} - Termin abgesagt',
      body_html: `<p>Liebe.r {{ exhibitor_name }},</p>
<p>Leider müssen wir Ihnen mitteilen, dass {{ visitor_company }} Ihren Termin abgesagt hat.</p>
<p><strong>Zeit: {{ appointment_time }}<br>Aussteller: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Besucher: {{ visitor_name }} - {{ visitor_company }}<br>Datum: {{ city }}<br>Grund: {{ reason }}.</strong></p>
<p><br>Mit freundlichen Grüßen,<br>Atout France</p>`,
    },
    en: {
      subject: '{{ event_title }} - Cancel Appointment',
      body_html: `<p>Dear {{ exhibitor_name }},</p>
<p>We are sorry to inform you that {{ visitor_company }} has canceled your appointment.</p>
<p><strong>Time: {{ appointment_time }}<br>Exhibitor: {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visitor: {{ visitor_name }} - {{ visitor_company }}<br>Date: {{ city }}<br>Reason: {{ reason }}.</strong></p>
<p><br>Best regards,<br>Atout France</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Rendez-vous annulé',
      body_html: `<p>Cher/Chère {{ exhibitor_name }},</p>
<p>Nous sommes désolés de vous informer que {{ visitor_company }} a annulé votre rendez-vous.</p>
<p><strong>Heure : {{ appointment_time }}<br>Exposant : {{ exhibitor_name }} - {{ exhibitor_company }}<br>Visiteur : {{ visitor_name }} - {{ visitor_company }}<br>Date : {{ city }}<br>Motif : {{ reason }}.</strong></p>
<p><br>Cordialement,<br>Atout France</p>`,
    },
  },

  // register-new_save.php — internal notification to the Atout France team
  // that a new visitor registration request was submitted.
  'register-new-request': {
    de: {
      subject: '{{ event_title }} - Neue Registrierungsanfrage',
      body_html: `<p>Liebes Atout France Team,</p>
<p>Eine neue Registrierungsanfrage wurde erstellt:</p>
<p><strong>Besucher: {{ company }}<br>Adresse: {{ address }}<br>Stadt: {{ city }}<br>Land: {{ country }}<br>Firmentelefon: {{ company_phone }}<br>Firmen-E-Mail: {{ company_mail }}<br>Website: {{ website }}<br>Titel: {{ title }}<br>Nachname: {{ last_name }}<br>Vorname: {{ first_name }}<br>Position: {{ position }}<br>Telefon: {{ contact_phone }}<br>E-Mail: {{ contact_mail }}</strong></p>
<p><br>Mit freundlichen Grüßen</p>`,
    },
    en: {
      subject: '{{ event_title }} - New Registration Request',
      body_html: `<p>Dear Atout France Team,</p>
<p>A new registration request has been created:</p>
<p><strong>Visitor: {{ company }}<br>Address: {{ address }}<br>City: {{ city }}<br>Country: {{ country }}<br>Company Phone: {{ company_phone }}<br>Company E-Mail: {{ company_mail }}<br>Website: {{ website }}<br>Title: {{ title }}<br>Lastname: {{ last_name }}<br>Firstname: {{ first_name }}<br>Position: {{ position }}<br>Phone: {{ contact_phone }}<br>E-Mail: {{ contact_mail }}</strong></p>
<p><br>Best regards</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Nouvelle demande d\'inscription',
      body_html: `<p>Cher équipe Atout France,</p>
<p>Une nouvelle demande d'inscription a été créée :</p>
<p><strong>Visiteur : {{ company }}<br>Adresse : {{ address }}<br>Ville : {{ city }}<br>Pays : {{ country }}<br>Téléphone société : {{ company_phone }}<br>E-mail société : {{ company_mail }}<br>Site web : {{ website }}<br>Titre : {{ title }}<br>Nom : {{ last_name }}<br>Prénom : {{ first_name }}<br>Fonction : {{ position }}<br>Téléphone : {{ contact_phone }}<br>E-mail : {{ contact_mail }}</strong></p>
<p><br>Cordialement</p>`,
    },
  },

  // forgot-username_request.php — internal notification to the Atout France
  // team that a user requested their identification data.
  'forgot-username': {
    de: {
      subject: '{{ event_title }} - Anfrage Zugangsdaten',
      body_html: `<p>Hallo Atout France Team,</p>
<p>Auf der Website wurde eine neue Anfrage nach Zugangsdaten gestellt.</p>
<p>Angegebene E-Mail: {{ email }}</p>
<p>Das System hat den entsprechenden Benutzer gefunden:</p>
<p>Name: <strong>{{ name }}</strong><br>- Benutzername: <strong>{{ username }}</strong><br>- Passwort: <strong>{{ password }}</strong><br>- <strong>{{ user_type }}</strong></p>
<p><br>Mit freundlichen Grüßen</p>`,
    },
    en: {
      subject: '{{ event_title }} - Request Identification Data',
      body_html: `<p>Hello Atout France Team,</p>
<p>A new identification data request was made on the website.</p>
<p>Given E-Mail: {{ email }}</p>
<p>The system found the corresponding user:</p>
<p>Name: <strong>{{ name }}</strong><br>- Username: <strong>{{ username }}</strong><br>- Password: <strong>{{ password }}</strong><br>- <strong>{{ user_type }}</strong></p>
<p><br>Best regards</p>`,
    },
    fr: {
      subject: '{{ event_title }} - Demande de données d\'identification',
      body_html: `<p>Bonjour équipe Atout France,</p>
<p>Une nouvelle demande de données d'identification a été faite sur le site web.</p>
<p>E-mail fourni : {{ email }}</p>
<p>Le système a trouvé l'utilisateur correspondant :</p>
<p>Nom : <strong>{{ name }}</strong><br>- Identifiant : <strong>{{ username }}</strong><br>- Mot de passe : <strong>{{ password }}</strong><br>- <strong>{{ user_type }}</strong></p>
<p><br>Cordialement</p>`,
    },
  },
};
